import { useSyncExternalStore } from 'react'

import { getApiBaseUrl, getDefaultApiBaseUrl, isApiBaseUrlLocked, setApiBaseUrl, subscribeToApiBaseUrl } from '@/lib/api'

export type BackendServerOption = {
  name: string
  url: string
}

const BACKEND_SERVER_CONFIG_PATH = String(import.meta.env.VITE_BACKEND_SERVER_CONFIG_PATH || '').trim()
const LOCAL_BACKEND_HOST_PATTERN = /^(https?:\/\/)?(localhost|127\.0\.0\.1|\[::1\])(:\d+)?\/?$/i

function normalizeServerUrl(value: unknown): string {
  return String(value ?? '').trim().replace(/\/+$/, '')
}

function isLocalBackendUrl(value: string): boolean {
  return LOCAL_BACKEND_HOST_PATTERN.test(value.trim())
}

function isBackendServerOption(value: unknown): value is BackendServerOption {
  if (!value || typeof value !== 'object') {
    return false
  }

  const candidate = value as Record<string, unknown>
  return typeof candidate.name === 'string' && normalizeServerUrl(candidate.url).length > 0
}

export function getBackendServerConfigPath(): string {
  if (isApiBaseUrlLocked()) {
    return ''
  }
  return BACKEND_SERVER_CONFIG_PATH
}

export function useSelectedBackendServerUrl(): string {
  return useSyncExternalStore(subscribeToApiBaseUrl, getApiBaseUrl, getDefaultApiBaseUrl)
}

export function updateSelectedBackendServer(url: string): string {
  if (isApiBaseUrlLocked()) {
    return getDefaultApiBaseUrl()
  }
  return setApiBaseUrl(url)
}

export async function loadBackendServerOptions(): Promise<BackendServerOption[]> {
  if (isApiBaseUrlLocked() || !BACKEND_SERVER_CONFIG_PATH) {
    return []
  }

  const response = await fetch(BACKEND_SERVER_CONFIG_PATH, {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
    },
  })

  if (!response.ok) {
    throw new Error(`Unable to load backend server list from ${BACKEND_SERVER_CONFIG_PATH}`)
  }

  const payload: unknown = await response.json()

  if (!Array.isArray(payload)) {
    throw new Error('Backend server configuration must be a JSON array.')
  }

  return payload
    .filter(isBackendServerOption)
    .map((server) => ({
      name: server.name.trim(),
      url: normalizeServerUrl(server.url),
    }))
    .filter((server) => (
      server.name.length > 0
      && server.url.length > 0
      && !isLocalBackendUrl(server.url)
    ))
}